import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered instead of `children` once a descendant has thrown. */
  fallback: React.ReactNode;
  /** Name used in the console message, e.g. "Map". */
  label: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Stops a crash inside one subtree from taking down the whole app.
 *
 * Without a boundary, an error thrown during a child's render or
 * componentDidMount propagates to the root and React unmounts the ENTIRE tree,
 * leaving a completely blank page. That is exactly what happened on
 * 2026-08-13: react-map-gl threw "`width` must be supplied" from
 * componentDidMount and the whole site went blank in Firefox 153 and Edge 151.
 *
 * Wrapping the map means the worst case is a missing map, not a dead site.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    const { label } = this.props;
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary] ${label} failed to render:`, error);
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children, fallback } = this.props;
    if (hasError) {
      return fallback;
    }
    return children;
  }
}

export default ErrorBoundary;
